import { useState } from 'react'

import Toast from 'components/atoms/Toast'
import DashboardContent from 'components/lab/atoms/DashboardContent'
import { confirm } from 'components/molecules/Confirmation'
import { UserPermission } from 'components/lab/user/permissions'
import { useLabPermission } from 'hooks/useLabPermission'
import {
  ReaderAppVersion,
  useCreateReaderAppVersionMutation,
  useDeleteReaderAppVersionMutation,
  useGetReaderAppVersionsQuery,
} from 'store/services/readerApps'

type MutationError = {
  data?: {
    message?: string
  }
}

const emptyForm = {
  version: '',
  type: 'bin' as 'bin' | 'script',
  s3FileKey: '',
}

const suggestedFileKey = (version: string, type: 'bin' | 'script') =>
  version ? `reader-app-${version}.${type === 'script' ? 'js' : 'bin'}` : ''

const formatDate = (value: string) => (value ? new Date(value).toLocaleString() : '')

const ReaderAppSettings = () => {
  const canAdd = useLabPermission(UserPermission.ADD_READER_APP_VERSION)
  const canRemove = useLabPermission(UserPermission.REMOVE_READER_APP_VERSION)

  const { data: versions, isLoading } = useGetReaderAppVersionsQuery()
  const [createVersion, { isLoading: creating }] = useCreateReaderAppVersionMutation()
  const [deleteVersion, { isLoading: deleting }] = useDeleteReaderAppVersionMutation()

  const [form, setForm] = useState(emptyForm)

  const handleAdd = async () => {
    const version = form.version.trim()
    const s3FileKey = form.s3FileKey.trim() || suggestedFileKey(version, form.type)

    if (!version.match(/^[0-9]+\.[0-9]+(\.[0-9]+)?(\.[0-9]+)?$/)) {
      Toast({ type: 'error', label: 'Please enter a valid version, e.g. 8.0.1' })
      return
    }

    try {
      await confirm('', {
        title: 'Register Version',
        description: `Register version ${version} (${form.type}, ${s3FileKey})? Every Reader will download it on next start.`,
        yesAction: 'Yes, register',
        noAction: 'Cancel',
      })
    } catch {
      return // cancelled
    }

    try {
      await createVersion({ version, type: form.type, s3FileKey }).unwrap()
      Toast({ type: 'success', label: `Version ${version} registered. Readers will download it on next start.` })
      setForm(emptyForm)
    } catch (error: unknown) {
      Toast({
        type: 'error',
        label: (error as MutationError)?.data?.message || 'Failed to register the version.',
      })
    }
  }

  const handleDelete = async (entry: ReaderAppVersion) => {
    try {
      await confirm('', {
        title: 'Remove Version',
        description: `Remove version ${entry.version} (${entry.type})? Readers will fall back to the next most recent version.`,
        yesAction: 'Yes, remove',
        noAction: 'Cancel',
        yesColor: 'danger',
      })
    } catch {
      return // cancelled
    }
    try {
      await deleteVersion(entry.id).unwrap()
      Toast({ type: 'success', label: `Version ${entry.version} removed.` })
    } catch (error: unknown) {
      Toast({
        type: 'error',
        label: (error as MutationError)?.data?.message || 'Failed to remove the version.',
      })
    }
  }

  return (
    <DashboardContent
      title="READER APP"
      icon="fas fa-tablet-alt"
      content={
        <div className="card card-primary">
          <div className="card-header">
            <h3 className="card-title">Desktop App Versioning</h3>
          </div>
          <div className="card-body">
            <div className="alert alert-info">
              The most recent registered version is what every TestSmarter Reader downloads on
              start. The build file must already be uploaded to the Reader S3 bucket — registration
              is rejected if the file is missing.
            </div>

            {isLoading ? (
              <div>Loading...</div>
            ) : (
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Type</th>
                    <th>S3 File Key</th>
                    <th>Registered</th>
                    {canRemove && <th style={{ width: 100 }} />}
                  </tr>
                </thead>
                <tbody>
                  {(versions ?? []).map((entry) => (
                    <tr key={entry.id}>
                      <td>{entry.version}</td>
                      <td>{entry.type}</td>
                      <td>{entry.s3FileKey}</td>
                      <td>{formatDate(entry.createdAt)}</td>
                      {canRemove && (
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            disabled={deleting}
                            onClick={() => handleDelete(entry)}
                          >
                            Remove
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {!versions?.length && (
                    <tr>
                      <td colSpan={canRemove ? 5 : 4}>No versions registered.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {canAdd && (
              <>
                <hr />
                <h5>Add version</h5>
                <div className="row">
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Version</label>
                      <input
                        className="form-control"
                        placeholder="e.g. 8.0.1"
                        value={form.version}
                        onChange={(e) => setForm({ ...form, version: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-group">
                      <label>Type</label>
                      <select
                        className="form-control"
                        value={form.type}
                        onChange={(e) => setForm({ ...form, type: e.target.value as 'bin' | 'script' })}
                      >
                        <option value="bin">bin</option>
                        <option value="script">script</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>S3 File Key</label>
                      <input
                        className="form-control"
                        placeholder={suggestedFileKey(form.version.trim(), form.type) || 'reader-app-<version>.bin'}
                        value={form.s3FileKey}
                        onChange={(e) => setForm({ ...form, s3FileKey: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="col-md-3 d-flex align-items-end">
                    <div className="form-group">
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={creating || !form.version.trim()}
                        onClick={handleAdd}
                      >
                        {creating ? 'Registering...' : 'Add Version'}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      }
    />
  )
}

export default ReaderAppSettings
